import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { readError } from "../../api/client";
import { platformApi } from "../../api/platformClient";
import { ErrorNote } from "../../components/ErrorNote";

/**
 * One clinic company: its clinics, its staff, and what Healtrics did to it.
 *
 * The history is ours and not theirs. What happens inside their clinics
 * belongs to them, and listing it here would be reaching across the wall by
 * another route (ADR-024 section 5).
 */

interface Detail {
  id?: string;
  name?: string;
  created_at?: string;
  seat_count?: number;
  clinics?: { id?: string; name?: string; code?: string; phone?: string; address?: string }[];
  staff?: { full_name?: string; email?: string; is_org_admin?: boolean }[];
  history?: {
    action?: string;
    actor_type?: string;
    metadata?: Record<string, unknown>;
    created_at?: string;
  }[];
}

// What an audit action means to somebody reading it, rather than its key.
const SAID: Record<string, string> = {
  organization_created: "Clinic company created",
  clinic_created: "Clinic opened",
  staff_renamed: "A colleague was renamed",
  clinic_updated: "A clinic's details changed",
  platform_admin_created: "A Healtrics administrator was created",
};

function when(iso?: string): string {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-MY", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function OneOrganisation() {
  const { id } = useParams();
  const [detail, setDetail] = useState<Detail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let live = true;

    void (async () => {
      const { data, error: failed } = await platformApi.GET(
        "/platform/organizations/{id}",
        { params: { path: { id } } },
      );
      if (!live) return;
      setLoading(false);
      if (failed) {
        setError(readError(failed).message);
        return;
      }
      setDetail((data ?? null) as Detail | null);
    })();

    return () => {
      live = false;
    };
  }, [id]);

  if (loading) return <p className="empty">Reading the company…</p>;
  if (error) return <ErrorNote message={error} />;
  if (!detail) return <p className="empty">Nothing to show.</p>;

  return (
    <section>
      <p>
        <Link to=".." relative="path" className="link">
          &larr; Clinic companies
        </Link>
      </p>
      <h1 className="screen-head">{detail.name}</h1>
      <p className="screen-lead">
        {detail.clinics?.length ?? 0}{" "}
        {detail.clinics?.length === 1 ? "clinic" : "clinics"} &middot;{" "}
        {detail.seat_count ?? 0} {detail.seat_count === 1 ? "seat" : "seats"}
      </p>

      <div className="plat-columns">
        <div>
          <h2 className="plat-h2">Its clinics</h2>
          {detail.clinics?.length ? (
            <ul className="plat-list">
              {detail.clinics.map((clinic) => (
                <li key={clinic.id}>
                  <p className="plat-list-title">{clinic.name}</p>
                  <p className="muted">
                    {clinic.address || "No address recorded"}
                    {clinic.phone ? ` · ${clinic.phone}` : ""}
                  </p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="empty">No clinics yet. Its own admin opens the first.</p>
          )}

          <h2 className="plat-h2">Who works there</h2>
          {detail.staff?.length ? (
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.staff.map((person) => (
                    <tr key={person.email}>
                      <td>
                        {person.full_name}
                        {person.is_org_admin && (
                          <span className="tag">Organisation admin</span>
                        )}
                      </td>
                      <td className="muted">{person.email}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="empty">Nobody yet.</p>
          )}
        </div>

        <aside>
          <h2 className="plat-h2">What Healtrics did</h2>
          <p className="muted plat-aside-note">
            Our actions only. What happens inside their clinics is theirs, and
            it is not here.
          </p>
          {detail.history?.length ? (
            <ol className="plat-history">
              {detail.history.map((row, index) => (
                <li key={`${row.action}-${index}`}>
                  <span className="plat-when">{when(row.created_at)}</span>
                  <span>{SAID[row.action ?? ""] ?? row.action}</span>
                </li>
              ))}
            </ol>
          ) : (
            <p className="empty">Nothing recorded.</p>
          )}
        </aside>
      </div>
    </section>
  );
}
